/**
 * CE QU'ON DIT A QUELQU'UN QUI N'ENTRE PAS : la decision, isolee et pure.
 *
 * LE PROBLEME QU'ELLE REGLE. Depuis que l'ecran de connexion porte un bouton
 * Google, n'importe qui peut s'authentifier : un compte Google est gratuit, et
 * Firebase le signe sans rien demander a personne. S'authentifier n'est donc
 * plus entrer. La porte (`rattacherCompte`, cote serveur) rend un VERDICT, et
 * ce module dit ce que l'ecran en fait.
 *
 * TROIS CHOSES A NE JAMAIS CONFONDRE, et c'est tout l'objet de ce fichier :
 *   - « pas invite » est un FAIT, pas une panne. La personne a bien tape son
 *     mot de passe ou choisi son compte Google : lui montrer une erreur rouge
 *     lui fait croire qu'elle s'est trompee, et elle reessaie dix fois ;
 *   - « suspendu » est une DECISION de son organisation, et elle doit le
 *     comprendre sans appeler le support pour rien ;
 *   - « indisponible » est la SEULE vraie panne : la question n'a pas pu etre
 *     posee. Elle se reessaie, les deux autres non.
 *
 * CE QUE CE MODULE EST, ET N'EST PAS. Une fonction pure : ni React, ni
 * Firebase, ni horloge. Elle ne deconnecte personne, elle repond a « que
 * montre-t-on ? ». Meme doctrine que `connexionLocale.ts`, et pour la meme
 * raison : ca se teste sans navigateur, alors que l'ecran ne s'observe qu'a
 * l'ecran.
 */

/** Le verdict de la porte, tel que `@taki/auth` le rend. */
export interface VerdictEntree {
  statut: 'ok' | 'non-invite' | 'desactive' | 'sans-adresse' | 'indisponible';
  orgId: string;
  raison?: string | null;
}

/** Ce que l'ecran de refus affiche. */
export interface Refus {
  /** Le titre, court : ce qui se passe. */
  titre: string;
  /** La ligne d'explication, une phrase au plus. */
  detail: string;
  /**
   * Le geste « Reessayer » a-t-il un sens ? Il n'en a un que pour une panne :
   * reessayer un refus, c'est rejouer le meme refus, et laisser croire que
   * c'est un hasard.
   */
  peutReessayer: boolean;
}

/**
 * Faut-il barrer la route ?
 *
 * `null` (la question ne s'est pas posee) et `ok` laissent passer. Tout le
 * reste s'arrete ici : un jeton sans organisation ne peut RIEN lire de toute
 * facon, les rules le refuseraient une requete plus loin, avec un message que
 * personne ne comprendrait.
 */
export function entreeRefusee(verdict: VerdictEntree | null | undefined): boolean {
  if (verdict == null) return false;
  return verdict.statut !== 'ok';
}

/**
 * Le texte d'un refus, nom de l'organisation compris.
 *
 * @param verdict - le verdict de la porte.
 * @param nomOrg - le nom d'affichage de l'organisation (branding), a defaut
 *   son identifiant : on nomme TOUJOURS ou la personne a frappe, sinon un
 *   refus est indiscernable d'un autre quand on a deux environnements ouverts.
 */
export function texteDeRefus(verdict: VerdictEntree, nomOrg: string): Refus {
  switch (verdict.statut) {
    case 'non-invite':
      return {
        titre: 'Acces refuse',
        detail: `Cette adresse n'a pas de compte dans ${nomOrg}. Un administrateur peut l'ajouter.`,
        peutReessayer: false,
      };
    case 'desactive':
      return {
        titre: 'Acces suspendu',
        detail: `Cette adresse avait un compte dans ${nomOrg} : il a ete suspendu.`,
        peutReessayer: false,
      };
    case 'sans-adresse':
      return {
        titre: 'Adresse manquante',
        detail: "Ce compte n'expose pas d'adresse e-mail, et c'est elle qui donne l'acces.",
        peutReessayer: false,
      };
    default:
      return {
        titre: 'Verification impossible',
        detail: `L'acces a ${nomOrg} n'a pas pu etre verifie. Aucun acces n'est accorde tant que cette verification echoue.`,
        peutReessayer: true,
      };
  }
}
